export const en = {
    common: {
        appName: 'Supadown',
        accept: 'Accept',
        cancel: 'Cancel',
        loading: 'Loading...',
        back: 'Back',
        notFound: '404',
        notFoundBody: 'This page does not exist.',
        skipToContent: 'Skip to content',
        language: 'Language',
        toggleTheme: 'Toggle theme',
        themeLight: 'Light',
        themeDark: 'Dark',
        themeSystem: 'System',
    },
    nav: {
        legal: 'Terms & privacy',
        backHome: 'Back to home',
        howTo: 'How to get the link',
    },
    home: {
        tagline: 'Link in. Video out.',
        seoHeading: 'Download videos from YouTube, Instagram & TikTok — free online',
        seoDescription: 'Supadown is a free online video downloader. Save videos from YouTube, Instagram Reels, TikTok, Facebook, X (Twitter), LinkedIn, Kwai, and Pinterest. No install, no sign-up.',
        paste: 'Paste the video URL',
        analyze: 'Analyze',
        download: 'Download',
        downloading: 'Downloading...',
        invalidUrl: 'Invalid URL. Paste the full video link.',
        unsupported: 'Platform not yet supported.',
        serverError: 'Something went wrong. Please try again shortly.',
        details: 'Video details',
        channel: 'Channel',
        duration: 'Duration',
        views: 'Views',
        quality: 'Quality',
        supportedPlatforms: 'Supported platforms',
    },
    platforms: {
        items: {
            youtube: { name: 'YouTube', description: 'Public videos, Shorts and Lives' },
            instagram: { name: 'Instagram', description: 'Public posts, Reels and Stories' },
            facebook: { name: 'Facebook', description: 'Public Facebook videos' },
            twitter: { name: 'Twitter / X', description: 'Videos from public posts' },
            linkedin: { name: 'LinkedIn', description: 'Public posts and LinkedIn Learning' },
            tiktok: { name: 'TikTok', description: 'Public TikTok videos' },
            kwai: { name: 'Kwai', description: 'Public Kwai videos' },
            pinterest: { name: 'Pinterest', description: 'Video Pins' },
        },
    },
    legal: {
        pageTitle: 'Terms & privacy',
        intro: 'Supadown is intended for personal, educational, journalistic and archival use of public content. Respect each source platform\'s copyright and terms of service.',
        privacy: {
            title: 'Privacy',
            body: 'We do not store URLs after the download finishes. Logs are anonymized. IPs are truncated to /24 and discarded after 24 hours. No tracking cookies are used.',
        },
        contact: {
            title: 'Contact',
            body: 'Questions, abuse reports, or takedown requests: hello@supadown.com.',
        },
    },
    legalGate: {
        title: 'Before you continue',
        body: 'Supadown is intended for public content and legitimate use only (personal archiving, education, research, journalism). Respect each platform\'s copyright.',
        contactLine: 'Questions or takedown requests: hello@supadown.com.',
    },
    footer: {
        copyright: '© Supadown',
        contact: 'hello@supadown.com',
    },
    seo: {
        faqTitle: 'Frequently Asked Questions',
        faqGuideLink: 'See step-by-step guide →',
        faq: [
            { q: 'How to download YouTube videos?', a: 'Paste the YouTube link in the field above and click the search button. Supadown will analyze the video and show download options.' },
            { q: 'Is Supadown free?', a: 'Yes, Supadown is completely free. No registration, no hidden fees.' },
            { q: 'Does Supadown store my data?', a: 'No. We do not store any URLs or personal data after the download finishes. No tracking cookies are used.' },
            { q: 'Which platforms are supported?', a: 'YouTube, Instagram, TikTok, Facebook, X (Twitter), LinkedIn, Kwai, and Pinterest.' },
            { q: 'Do I need to install anything?', a: 'No. Supadown works directly in your browser. No apps, extensions, or software needed.' },
            { q: 'How to download Instagram Reels?', a: 'Copy the Reel link on Instagram, paste it in Supadown, and click the search button. Your download will be ready in seconds.' },
            { q: 'Does Supadown work on mobile?', a: 'Yes. Supadown works on any device with a browser — phone, tablet, or computer. No installation needed.' },
            { q: 'How to download TikTok videos?', a: 'Paste the TikTok link in Supadown. The video will be downloaded in the best quality available.' },
            { q: 'Can I download videos in HD or 4K?', a: 'Yes. Supadown shows all available quality options — from 360p up to 4K — so you can choose what you prefer.' },
            { q: 'How to download YouTube Shorts?', a: 'Copy the YouTube Short link and paste it in Supadown. Shorts are treated like any other video.' },
            { q: 'Does Supadown require sign-up?', a: 'No. No registration is needed. Just paste the link and download.' },
            { q: 'Can I download audio only?', a: 'Yes. Supadown shows separate audio options when available from the source platform.' },
        ],
    },
    howTo: {
        title: 'How to copy the video link',
        intro: 'To download a video, you need to copy its link from the original platform. Here\'s how to do it on each one:',
        tryIt: 'Download now',
        platforms: {
            youtube: {
                title: 'YouTube',
                steps: [
                    'Open the video on YouTube (app or browser).',
                    'Tap "Share" below the video.',
                    'Tap "Copy link".',
                    'Paste the link in Supadown and click the search button.',
                ],
            },
            instagram: {
                title: 'Instagram',
                steps: [
                    'Open the post or Reel on Instagram.',
                    'Tap the three dots (⋯) in the top-right corner.',
                    'Tap "Copy link".',
                    'Paste the link in Supadown.',
                ],
            },
            tiktok: {
                title: 'TikTok',
                steps: [
                    'Open the video on TikTok.',
                    'Tap the "Share" button (arrow icon).',
                    'Tap "Copy link".',
                    'Paste the link in Supadown.',
                ],
            },
            facebook: {
                title: 'Facebook',
                steps: [
                    'Open the video or Reel on Facebook.',
                    'Tap the three dots (⋯) in the top-right corner of the post.',
                    'Tap "Copy link".',
                    'Paste the link in Supadown.',
                ],
            },
            twitter: {
                title: 'X (Twitter)',
                steps: [
                    'Open the tweet with the video on X.',
                    'Tap the share button (arrow icon).',
                    'Tap "Copy link".',
                    'Paste the link in Supadown.',
                ],
            },
            linkedin: {
                title: 'LinkedIn',
                steps: [
                    'Open the post with the video on LinkedIn.',
                    'Tap the three dots (⋯) in the top-right corner.',
                    'Tap "Copy link to post".',
                    'Paste the link in Supadown.',
                ],
            },
            kwai: {
                title: 'Kwai',
                steps: [
                    'Open the video on Kwai.',
                    'Tap the "Share" button.',
                    'Tap "Copy link".',
                    'Paste the link in Supadown.',
                ],
            },
            pinterest: {
                title: 'Pinterest',
                steps: [
                    'Open the video Pin on Pinterest.',
                    'Tap the share button.',
                    'Tap "Copy link".',
                    'Paste the link in Supadown.',
                ],
            },
        },
    },
    platformSeo: {
        otherPlatforms: 'Other platforms',
        youtube: {
            chipLabel: 'YouTube',
            title: 'Download YouTube Videos Free',
            description: 'Download YouTube videos in HD and 4K for free. Supadown is the fastest and safest YouTube downloader. No install, no sign-up.',
            heading: 'Download YouTube videos — free & online',
            intro: 'Supadown lets you download YouTube videos in any quality, from 360p to 4K. Works with regular videos, Shorts, and public livestreams. Just paste the video link, pick your quality, and click download. No software or extensions to install.',
            cta: 'Download YouTube video now →',
            faq: [
                { q: 'Can I download YouTube videos in 4K?', a: 'Yes. Supadown shows all available resolutions including 720p, 1080p, 1440p, and 4K. Just pick the quality you want.' },
                { q: 'How to download YouTube Shorts?', a: 'Copy the Short link and paste it in Supadown. Shorts are treated like regular videos — pick your quality and download.' },
                { q: 'Can I download just the audio from a YouTube video?', a: 'Yes. Supadown shows separate audio options (M4A) when available, perfect for music and podcasts.' },
            ],
        },
        instagram: {
            chipLabel: 'Instagram',
            title: 'Download Instagram Videos Free',
            description: 'Download Reels, Stories, and Instagram videos for free. Fast downloader, no sign-up required. Works on mobile and desktop.',
            heading: 'Download Instagram videos & Reels — free & online',
            intro: 'Download Instagram videos including Reels, Stories, and video posts. Supadown analyzes the link and offers the download in the best quality available. Works on any device with a browser, no app needed.',
            cta: 'Download Instagram video now →',
            faq: [
                { q: 'Can I download Instagram Reels?', a: 'Yes. Copy the Reel link and paste it in Supadown. The video will be downloaded in original quality.' },
                { q: 'Can I download Instagram Stories?', a: 'Yes, as long as the profile is public. Copy the Story link and paste it in Supadown.' },
                { q: 'Does it work with private profiles?', a: 'No. Supadown only downloads content from public profiles. Private content requires authentication we don\'t provide.' },
            ],
        },
        tiktok: {
            chipLabel: 'TikTok',
            title: 'Download TikTok Videos Free',
            description: 'Download TikTok videos for free online. Fast downloader, no sign-up required.',
            heading: 'Download TikTok videos — free & online',
            intro: 'Download TikTok videos quickly with Supadown. Paste the video link, click search, and download in the best available quality. Works right in your browser, no app or extension needed.',
            cta: 'Download TikTok video now →',
            faq: [
                { q: 'Does the TikTok video come without watermark?', a: 'Supadown downloads the video in the best quality available from the platform. Watermark presence depends on the video source on TikTok.' },
                { q: 'Can I download TikTok on mobile?', a: 'Yes. Supadown works on any mobile browser — Chrome, Safari, Firefox, etc.' },
                { q: 'Do I need a TikTok account to download?', a: 'No. You just need the public video link. No account required.' },
            ],
        },
        facebook: {
            chipLabel: 'Facebook',
            title: 'Download Facebook Videos Free',
            description: 'Download Facebook videos and Reels for free. Fast online downloader, no sign-up required.',
            heading: 'Download Facebook videos — free & online',
            intro: 'Download public Facebook videos including Reels and page videos. Paste the video link in Supadown, choose your quality, and download. No install, no sign-up, no hassle.',
            cta: 'Download Facebook video now →',
            faq: [
                { q: 'Can I download Facebook Reels?', a: 'Yes. Copy the Reel link on Facebook and paste it in Supadown. It works like any other video download.' },
                { q: 'Does it work with group videos?', a: 'Only if the group and video are public. Private group videos cannot be accessed.' },
                { q: 'What quality is available?', a: 'Supadown shows all available qualities. Typically HD (720p) and Full HD (1080p).' },
            ],
        },
        twitter: {
            chipLabel: 'X (Twitter)',
            title: 'Download X (Twitter) Videos Free',
            description: 'Download X (Twitter) videos for free online. Fast downloader, no sign-up required.',
            heading: 'Download X (Twitter) videos — free & online',
            intro: 'Download videos from public tweets on X (formerly Twitter). Paste the tweet link containing the video in Supadown and download in your preferred quality. Fast, free, and no sign-up needed.',
            cta: 'Download X video now →',
            faq: [
                { q: 'How do I get the tweet link?', a: 'Open the tweet, tap the share button, then tap "Copy link". Paste the link in Supadown.' },
                { q: 'Does it work with protected tweets?', a: 'No. Only tweets from public accounts can be downloaded.' },
                { q: 'Can Twitter GIFs be downloaded?', a: 'Yes. GIFs on Twitter are actually short looping videos — Supadown downloads them as MP4.' },
            ],
        },
        linkedin: {
            chipLabel: 'LinkedIn',
            title: 'Download LinkedIn Videos Free',
            description: 'Download LinkedIn videos for free. Online downloader for public post videos.',
            heading: 'Download LinkedIn videos — free & online',
            intro: 'Download videos from public LinkedIn posts with Supadown. Paste the post link, analyze, and download. Perfect for saving presentations, webinars, and professional content.',
            cta: 'Download LinkedIn video now →',
            faq: [
                { q: 'Can I download LinkedIn Learning videos?', a: 'Supadown works with public LinkedIn posts. LinkedIn Learning videos are protected by subscription.' },
                { q: 'Does the video lose quality?', a: 'No. Supadown downloads at the original quality published by the author.' },
            ],
        },
        kwai: {
            chipLabel: 'Kwai',
            title: 'Download Kwai Videos Free',
            description: 'Download Kwai videos for free online. Fast downloader, no sign-up required.',
            heading: 'Download Kwai videos — free & online',
            intro: 'Download Kwai videos quickly with Supadown. Copy the video link from the Kwai app, paste it in Supadown, and download. No installation needed, works right in your browser.',
            cta: 'Download Kwai video now →',
            faq: [
                { q: 'How do I get the link from a Kwai video?', a: 'Open the video in the Kwai app, tap "Share", then tap "Copy link".' },
                { q: 'Does it work with Kwai videos from any country?', a: 'Yes. Supadown works with Kwai links from any region.' },
            ],
        },
        pinterest: {
            chipLabel: 'Pinterest',
            title: 'Download Pinterest Videos Free',
            description: 'Download videos and animated Pins from Pinterest for free. Online downloader, no sign-up required.',
            heading: 'Download Pinterest videos — free & online',
            intro: 'Download video Pins from Pinterest with Supadown. Copy the Pin link, paste it in Supadown, and download. Perfect for saving tutorials, recipes, and visual inspiration.',
            cta: 'Download Pinterest video now →',
            faq: [
                { q: 'Can I download image Pins?', a: 'Supadown specializes in videos. For images, use Pinterest\'s built-in save button.' },
                { q: 'Does it work with Idea Pins?', a: 'Yes, as long as the Idea Pin contains video. Paste the link in Supadown as usual.' },
            ],
        },
    },
};
